/**
 * The client half of the upload limits: the same field → form → default
 * chain the server resolves in `lib/uploadRules.ts`, read from the contract
 * both sides keep (`shared/uploadRules.json`). The check here is a courtesy —
 * a file is refused at the field, before submit starts — and the server's own
 * check is what decides.
 */
import rules from '../../../shared/uploadRules.json';
import type { FieldUploadMeta, FormUploadSettings } from '../shared/types';
import { STRINGS as S } from './strings';

export interface UploadLimits {
  maxFiles: number;
  maxSizeMb: number;
  /** lowercase, no dot */
  accept: string[];
}

export const DEFAULT_ACCEPT: readonly string[] = rules.allowed;
export const ACCEPT_PRESETS: Record<'documents' | 'images', readonly string[]> = rules.presets;
export const MAX_SIZE_MB_CEILING = rules.maxSizeMbCeiling;
export const MAX_FILES_CEILING = rules.maxFilesCeiling;
export const DEFAULT_MAX_SIZE_MB = rules.defaultMaxSizeMb;
export const DEFAULT_MAX_FILES = rules.defaultMaxFiles;

const allowed = new Set(DEFAULT_ACCEPT);

/** Lowercase, no dot, deduped, only what the allowlist permits. */
export function normalizeAccept(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (const v of raw) {
    const ext = String(v ?? '').trim().toLowerCase().replace(/^\./, '');
    if (ext && allowed.has(ext) && !out.includes(ext)) out.push(ext);
  }
  return out;
}

const clampInt = (v: unknown, ceiling: number): number | undefined => {
  const n = Math.floor(Number(v));
  if (!Number.isFinite(n) || n < 1) return undefined;
  return Math.min(ceiling, n);
};

export function resolveUploadLimits(field?: FieldUploadMeta | null, form?: FormUploadSettings | null): UploadLimits {
  const maxFiles = clampInt(field?.maxFiles, MAX_FILES_CEILING) ?? clampInt(form?.maxFiles, MAX_FILES_CEILING) ?? DEFAULT_MAX_FILES;
  const maxSizeMb = clampInt(field?.maxSizeMb, MAX_SIZE_MB_CEILING) ?? clampInt(form?.maxSizeMb, MAX_SIZE_MB_CEILING) ?? DEFAULT_MAX_SIZE_MB;
  const fieldAccept = normalizeAccept(field?.accept);
  const formAccept = normalizeAccept(form?.accept);
  const accept = fieldAccept.length ? fieldAccept : formAccept.length ? formAccept : [...DEFAULT_ACCEPT];
  return { maxFiles, maxSizeMb, accept };
}

/** True when the list is a real restriction rather than the whole allowlist. */
export const acceptRestricted = (accept: string[]): boolean => accept.length > 0 && accept.length < DEFAULT_ACCEPT.length;

export const extensionOf = (name: string): string => {
  const i = name.lastIndexOf('.');
  return i > 0 && i < name.length - 1 ? name.slice(i + 1).toLowerCase() : '';
};

export const isImageFile = (f: File): boolean =>
  (f.type ? f.type.startsWith('image/') : ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(extensionOf(f.name)));

/** "1.2 MB", "340 KB", "12 bytes" */
export function formatBytes(n: number): string {
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(n >= 10 * 1024 * 1024 ? 0 : 1)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} ${n === 1 ? 'byte' : 'bytes'}`;
}

/** The kind of refusal each file gets, or none. */
export type FileRefusal = 'size' | 'type' | 'count';

export interface FileCheck {
  /** the files that passed, in the order they came */
  accepted: File[];
  /** the first refusal's message, worded like the server's */
  error?: string;
  refused: { file: File; why: FileRefusal }[];
}

/**
 * Which of the incoming files may join the `already` picked ones. Size and
 * type are checked per file; the count is checked against what would be
 * attached after them. The message is the first problem found, in the order
 * the files came — one line under the control, as the U1 path renders it.
 */
export function checkFiles(incoming: File[], limits: UploadLimits, already = 0): FileCheck {
  const accepted: File[] = [];
  const refused: FileCheck['refused'] = [];
  const maxBytes = limits.maxSizeMb * 1024 * 1024;
  let error: string | undefined;
  const refuse = (file: File, why: FileRefusal, msg: string) => { refused.push({ file, why }); if (!error) error = msg; };
  for (const file of incoming) {
    if (file.size > maxBytes) { refuse(file, 'size', S.files.tooLarge(limits.maxSizeMb)); continue; }
    if (!limits.accept.includes(extensionOf(file.name))) {
      refuse(file, 'type', acceptRestricted(limits.accept) ? S.files.onlyTypes(limits.accept) : S.files.notAccepted);
      continue;
    }
    if (already + accepted.length >= limits.maxFiles) { refuse(file, 'count', S.files.tooMany(limits.maxFiles)); continue; }
    accepted.push(file);
  }
  return { accepted, error, refused };
}
